import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SPRINT 11 — Kontakt.
// Großes Nachtbild · beleuchtete BERLIN ─ CUBES · Menschen · Atmosphäre.
// Best-Match aus vorhandenen Renderings (Berlin bei Dämmerung/Nacht).
const NIGHT_BG =
  "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/zgiqmdrd_grafik.png";

export default function CTA() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", organization: "", role: "", message: "", subject: "" });
  const nameRef = useRef(null);

  useEffect(() => {
    const onPrefill = (e) => {
      setForm((f) => ({ ...f, message: e.detail.message || "", subject: e.detail.subject || "" }));
      setTimeout(() => nameRef.current?.focus({ preventScroll: true }), 700);
    };
    window.addEventListener("berlincube:prefill-contact", onPrefill);
    // Prefill via URL (z. B. von Anwendungs-Landingpages): /?anliegen=foerderleitfaden#contact
    const params = new URLSearchParams(window.location.search);
    if (params.get("anliegen") === "foerderleitfaden") {
      const isEn = window.location.pathname.startsWith("/en");
      setForm((f) => ({
        ...f,
        message: isEn
          ? "Please send me the funding guide for municipalities."
          : "Bitte senden Sie mir den Förderleitfaden für Kommunen.",
        subject: "Förderleitfaden",
      }));
      setTimeout(() => {
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
        nameRef.current?.focus({ preventScroll: true });
      }, 600);
    }
    return () => window.removeEventListener("berlincube:prefill-contact", onPrefill);
  }, []);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      toast.success(t.contact.form.success);
      setForm({ name: "", email: "", organization: "", role: "", message: "", subject: "" });
    } catch {
      toast.error(t.contact.form.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-transparent border-0 border-b border-white/25 rounded-none px-0 h-12 text-white placeholder:text-white/30 focus-visible:border-[#F5D77A] focus-visible:ring-0 focus-visible:ring-offset-0";

  return (
    <section
      id="contact"
      data-testid="section-contact"
      className="relative overflow-hidden"
    >
      {/* SPRINT 11 · GROSSES NACHTBILD */}
      <div className="absolute inset-0 z-0">
        <img
          src={NIGHT_BG}
          alt="Berlin bei Nacht · beleuchtete Berlin-Cubes"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D]/85 to-[#0A0B0D]/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0B0D]/80 via-transparent to-[#0A0B0D]/60" />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-14 py-32 md:py-48">
        {/* Hero headline — very big, centered-ish */}
        <div className="mb-16 md:mb-24 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-[#F5D77A]/80 mb-10">
              <span className="h-px w-8 bg-[#F5D77A]" />
              {t.contact.kicker}
            </div>
            <h2
              className="font-display font-light tracking-[-0.035em] leading-[0.95] text-white"
              style={{ fontSize: "clamp(2.75rem, 7.5vw, 7rem)" }}
              data-testid="contact-headline"
            >
              {t.contact.title}
            </h2>
            <p className="mt-10 text-white/70 text-lg md:text-xl leading-relaxed max-w-2xl">
              {t.contact.body}
            </p>
          </motion.div>
        </div>

        {/* Form + Direct panel */}
        <div className="grid lg:grid-cols-12 gap-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="lg:col-span-4 flex flex-col justify-between"
          >
            <div className="border border-white/15 bg-black/40 backdrop-blur-md p-8 md:p-10">
              <div className="text-[10px] tracking-[0.35em] uppercase text-white/50 mb-6">
                {lang === "en" ? "Direct" : "Direkt"}
              </div>
              <a
                href="mailto:info@berlin-cube.de"
                translate="no"
                className="notranslate block font-display text-2xl md:text-3xl text-white hover:text-[#F5D77A] transition-colors w-fit"
                data-testid="contact-email"
              >
                info@berlin-cube.de
              </a>
              <a
                href="https://www.berlin-cube.de"
                translate="no"
                target="_blank"
                rel="noopener noreferrer"
                className="notranslate mt-3 block text-white/60 hover:text-white text-sm w-fit transition-colors"
                data-testid="contact-website"
              >
                www.berlin-cube.de
              </a>

              <div className="mt-10 pt-6 border-t border-white/10 flex flex-wrap items-center gap-x-6 gap-y-2 text-[10px] tracking-[0.35em] uppercase text-white/40 font-display">
                <span>DEKRA IWA 14-1</span>
                <span className="text-white/15">·</span>
                <span>EU EP 3 486 374</span>
              </div>
            </div>
          </motion.div>

          {/* FORM */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="lg:col-span-8"
          >
            <form
              onSubmit={onSubmit}
              className="border border-white/15 bg-black/40 backdrop-blur-md p-8 md:p-12 space-y-6"
              data-testid="contact-form"
            >
              <input type="hidden" value={form.subject} readOnly data-testid="contact-input-subject" />
              <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                <div>
                  <label className="text-[10px] tracking-[0.3em] uppercase text-white/45">
                    {t.contact.form.name}
                  </label>
                  <Input
                    required
                    ref={nameRef}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputCls}
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.3em] uppercase text-white/45">
                    {t.contact.form.email}
                  </label>
                  <Input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className={inputCls}
                    data-testid="contact-input-email"
                  />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.3em] uppercase text-white/45">
                    {t.contact.form.org}
                  </label>
                  <Input
                    value={form.organization}
                    onChange={(e) => setForm({ ...form, organization: e.target.value })}
                    className={inputCls}
                    data-testid="contact-input-org"
                  />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.3em] uppercase text-white/45">
                    {t.contact.form.role}
                  </label>
                  <Input
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className={inputCls}
                    data-testid="contact-input-role"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.3em] uppercase text-white/45">
                  {t.contact.form.message}
                </label>
                <Textarea
                  required
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="bg-transparent border-0 border-b border-white/25 rounded-none px-0 pt-2 text-white placeholder:text-white/30 focus-visible:border-[#F5D77A] focus-visible:ring-0 resize-none"
                  data-testid="contact-input-message"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="group inline-flex items-center justify-between px-6 py-4 bg-[#F5D77A] text-[#1F2328] font-medium tracking-wide hover:bg-white transition-colors disabled:opacity-60"
                >
                  <span>{loading ? t.contact.form.sending : t.contact.cta1}</span>
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/pitch.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-between px-6 py-4 border border-white/30 text-white hover:bg-white hover:text-[#1F2328] transition-colors"
                  data-testid="contact-download-pdf"
                >
                  <span>{t.contact.cta2}</span>
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
